import { Icon } from "@iconify/react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useI18n } from "@/locales/i18n-provider";
import { NavigationList } from "@/surfaces/standalone/components/navigation-list";
import { navigationGroups } from "@/surfaces/standalone/navigation/navigation-model";
import { useVisibleNavigationGroups } from "@/surfaces/standalone/navigation/use-navigation";
import { useNavigationBadges } from "@/surfaces/standalone/navigation/use-navigation-badges";
import { cn } from "@/utils";

type WorkspaceSidebarProps = {
  collapsed?: boolean;
  onNavigate?: () => void;
  onToggle: () => void;
  toggleLabel: string;
};

export function WorkspaceSidebar({ collapsed = false, onNavigate, onToggle, toggleLabel }: WorkspaceSidebarProps) {
  const { t } = useI18n();
  const groups = useNavigationBadges(useVisibleNavigationGroups(navigationGroups));

  return (
    <TooltipProvider key={collapsed ? "collapsed" : "expanded"} delayDuration={100}>
      <div className="flex h-full flex-col text-sidebar-foreground">
        <nav className={cn("flex-1 overflow-x-hidden overflow-y-auto", collapsed ? "space-y-3 p-2" : "space-y-6 p-3")} aria-label={t("shell.mainNavigation")}>
          {groups.map((group, index) => (
            <section key={group.labelKey}>
              {collapsed && index > 0 ? (
                <Separator className="mb-2" />
              ) : !collapsed ? (
                <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">{t(group.labelKey)}</p>
              ) : null}
              <NavigationList collapsed={collapsed} items={group.items} onNavigate={onNavigate} />
            </section>
          ))}
        </nav>

        <div className={cn("shrink-0", collapsed ? "p-2" : "p-3")}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={toggleLabel}
                className={cn("h-9 w-full gap-3 rounded-xl text-muted-foreground", collapsed ? "justify-center px-0" : "justify-start px-3")}
                variant="ghost"
                onClick={onToggle}
              >
                <Icon className="size-4" icon={collapsed ? "tabler:layout-sidebar-left-expand" : "tabler:layout-sidebar-left-collapse"} />
                {collapsed ? null : <span className="text-xs">{toggleLabel}</span>}
              </Button>
            </TooltipTrigger>
            {collapsed ? (
              <TooltipContent side="right" sideOffset={8}>
                {toggleLabel}
              </TooltipContent>
            ) : null}
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}
